import React, { Component, Fragment } from 'react';

import classNames from 'classnames';
import { I18n } from 'components/translate';
import PropTypes from 'prop-types';

import ModalBody from './ModalBody';
import ModalClose from './ModalClose';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';

import './Modal.scss';

const Context = React.createContext();

export const STATUS = {
  CLOSED: 'closed',
  CLOSING: 'closing',
  OPENING: 'opening',
  OPENED: 'opened'
};

export default class Modal extends Component {
  state = {
    status: STATUS.CLOSED
  };

  static propTypes = {
    portal: PropTypes.string,
    relative: PropTypes.bool,
    hideWarn: PropTypes.bool,
    onShow: PropTypes.func,
    onClose: PropTypes.func,
    title: PropTypes.string,
    headerTitle: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    fullSize: PropTypes.bool,
    subtitle: PropTypes.string,
    clickOutsideShouldNotClose: PropTypes.bool
  };

  static defaultProps = {
    relative: false,
    portal: 'app',
    fullSize: false,
    headerTitle: ''
  };

  static Consumer = Context.Consumer;

  static Header = ModalHeader;
  static Body = ModalBody;
  static Footer = ModalFooter;
  static Close = ModalClose;

  constructor(props) {
    super(props);
    this.el =
      document.getElementById(props.portal) || document.createElement('div');
  }

  enableOverflow = () => {
    document.body.style.overflow = 'auto';
  };

  disableOverflow = () => {
    document.body.style.overflow = 'hidden';
  };

  componentDidMount() {
    this.disableOverflow();
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    setTimeout(this.toggleModal, 100);
  }

  componentWillUnmount() {
    this.enableOverflow();
  }

  onClose = () => {
    this.enableOverflow();
    const { onClose } = this.props;

    onClose && onClose();
    this.setState({ status: STATUS.CLOSED });
  };

  onShow = () => {
    this.disableOverflow();
    const { onShow } = this.props;

    onShow && onShow();
    this.setState({ status: STATUS.OPENED });
  };

  onAnimationEnd = () => {
    const { status } = this.state;

    if (status === STATUS.OPENING) {
      this.onShow();
    } else if (status === STATUS.CLOSING) {
      this.onClose();
    }
  };

  toggleModal = (show = true) => {
    show ? this.disableOverflow() : this.enableOverflow();

    this.setState({
      status: show ? STATUS.OPENING : STATUS.CLOSING
    });
  };

  getContext = () => ({
    actions: {
      openModal: this.toggleModal.bind(this, true),
      closeModal: this.toggleModal.bind(this, false)
    }
  });

  outsideDivClick = () => {
    const { clickOutsideShouldNotClose } = this.props;

    !clickOutsideShouldNotClose && this.toggleModal(false);
  };

  renderModal() {
    const {
      relative,
      className,
      title,
      children,
      fullSize,
      headerTitle,
      hideWarn
    } = this.props;
    const { status } = this.state;
    const context = this.getContext();
    const classModal = classNames(
      'modal modal-backdrop',
      {
        'fade': [STATUS.CLOSING, STATUS.CLOSED].includes(status),
        'show': [STATUS.OPENING, STATUS.OPENED].includes(status),
        'modal--full': fullSize
      },
      className
    );

    const style = { display: 'block' };

    if (relative) {
      style.position = 'relative';
    }

    return (
      <Context.Provider value={context}>
        <div
          ref={(node) => (this.modal = node)}
          onTransitionEnd={this.onAnimationEnd}
          onClick={this.outsideDivClick}
          tabIndex="-1"
          role="dialog"
          style={style}
          className={classModal}
          // eslint-disable-next-line react/prop-types
          data-test={this.props['data-test'] || 'c-modal'}
        >
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              {title && (
                <ModalHeader>
                  <ModalClose />

                  {typeof title === 'string' && (
                    <Fragment>
                      {!hideWarn && (
                        <h5 className="modal-header-warning">
                          <I18n label="general.warn_title" />
                        </h5>
                      )}
                      {headerTitle && (
                        <h5 className="modal-header-warning">
                          <I18n label={headerTitle} />
                        </h5>
                      )}
                      <h3 className="modal-title">
                        <I18n label={title} />
                      </h3>
                    </Fragment>
                  )}
                </ModalHeader>
              )}
              {children}
            </div>
          </div>
        </div>
      </Context.Provider>
    );
  }

  render() {
    return <div>{this.renderModal()}</div>;
  }
}
